import * as React from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { contentfulAPIURL } from '@/clients/contentful';
import { GraphQLError } from 'graphql';
import { createApolloClient } from '@/clients/apollo';
import {
  queryNewsPageCollectionWithSlugsAndIds,
  queryNewsPageBySlug,
  queryFooter,
  queryNavbar,
} from '@/clients/queries';
import { FooterQuery, NavbarQuery, NewsPageBySlugQuery } from '../../../@types/generated';
import NewsArticlePage from '../../contentful/NewsArticlePage';
import { getOr } from '@/utils/options';
import PageContainer from '@/components/PageContainer';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { Log } from '@/logging/datadog';

type Props = {
  query: NewsPageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const RouteComponent = ({ query, navbar, footer, radioSchedules }: Props) => {
  const newsPage = query.newsPageCollection?.items[0];

  if (!newsPage) {
    throw new Error('no news page collection found');
  }

  const title = `Sýn | ${newsPage.newsArticle?.title}`;

  const metaTags = {
    title,
    description: getOr(newsPage.newsArticle?.description, 'Frétt'),
    url: `https://www.syn.is/frettir/${newsPage.slug}`,
    keywords: getOr(newsPage.title, 'syn,frétt'),
    image: {
      url: getOr(newsPage.newsArticle?.heroImage?.image?.url, ''),
    },
  };

  return (
    <PageContainer title={title} metaTags={metaTags} navbar={navbar} footer={footer} radioSchedules={radioSchedules}>
      <NewsArticlePage query={query} />
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const slug = (params?.slug ? [params.slug].flat() : []).join('/');
    const contentfulPage = await queryNewsPageBySlug(apolloClient, slug);
    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    return {
      props: {
        navbar: navbar.data,
        query: contentfulPage.data,
        footer: footer.data,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apiUrl = contentfulAPIURL(
    process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
    process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
  );
  const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

  const res = await queryNewsPageCollectionWithSlugsAndIds(apolloClient, 0, 1000);

  if (res.error) {
    Log.error(res.error.message || '', res.error);
    throw res.error;
  }

  if (res.errors) {
    Log.errors(
      'GraphQL errors',
      res.errors.map((error) => new GraphQLError(error.message))
    );
    throw new Error(`error when performing graphql query: ${res.errors.map((x) => x.toString()).join('\n')}`);
  }

  if (!res.data.newsPageCollection) {
    Log.error('error', new Error('unable to find page collection in response'));
    throw new Error('unable to find page collection in response');
  }

  const slugs = res.data.newsPageCollection.items.reduce<string[]>((acc, curr) => {
    if (curr?.slug) {
      acc.push(curr.slug);
    }
    return acc;
  }, []);

  return {
    paths: slugs.map((slug) => {
      return {
        params: { slug: slug },
      };
    }),
    fallback: false,
  };
};

export default RouteComponent;
